import { Box, InputBase, IconButton, Badge } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { colors, spacing, radius, TypographyVariant } from 'common/theme';
import { IconHot } from 'components/common/IconHot';
import SearchIconSvg from 'assets/personal_zone/Search.svg';
import FilterIconSvg from 'assets/personal_zone/Filter.svg';
import { CouponTab } from './CouponTab';
import { COUPON_TABS, FILTER_BENEFITS_TYPES } from '../../utils/enums';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';

const getPaidTabLabel = (selectedFilter, translate) => {
    if (selectedFilter === FILTER_BENEFITS_TYPES.ARCHIVED) {
        return translate('my_coupons_personal_info_paid_archived_coupons_header');
    }
    return translate('my_coupons_personal_info_paid_coupons_header');
};

const getDownloadedTabLabel = (selectedFilter, translate) => {
    if (selectedFilter === FILTER_BENEFITS_TYPES.ARCHIVED) {
        return translate('my_coupons_personal_info_downloaded_archived_coupons_header');
    }
    return translate('my_coupons_personal_info_free_coupons_header');
};

export const TabsSearchHeader = ({ testID, paidCount, downloadedCount, filterHook }) => {
    const { translate } = useTranslations();
    const selectedFilter = filterHook.appliedPaidFilters?.selectedFilter;
    const paidTabLabel = getPaidTabLabel(selectedFilter, translate);
    const downloadedTabLabel = getDownloadedTabLabel(selectedFilter, translate);
    return (
        <Box testId={testID} dir="rtl" sx={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', overflow: 'hidden' } }>
            <Box testId={generateTestId(testID, 'header-container')} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: `${spacing.md}px`, flexWrap: { xs: 'wrap', sm: 'nowrap', md: 'nowrap', lg: 'nowrap' },  paddingBottom:`${spacing.xl}px` }}>
                <Box testId={generateTestId(testID, 'tabs-container')} sx={{ display: 'flex', alignItems: 'stretch', gap: { xs: `${spacing.md}px`, sm: `${spacing.xl}px`, md: `${spacing.xl}px` }, width: { xs: '100%', sm: 'auto', md: 'auto' }, flexShrink: { xs: 0, sm: 0, md: 0 }, minWidth: { xs: 0, sm: 'fit-content', md: 'fit-content' }, justifyContent: { xs: 'space-between', sm: 'flex-start', md: 'flex-start', lg: 'flex-start' }, overflow: 'visible' }}>
                <CouponTab testID={generateTestId(testID, 'paid-tab')} label={paidTabLabel} count={paidCount} isActive={filterHook.activeTab === COUPON_TABS.PAID} onClick={() => filterHook.setActiveTab(COUPON_TABS.PAID)} />
                <CouponTab testID={generateTestId(testID, 'downloaded-tab')} label={downloadedTabLabel} count={downloadedCount} isActive={filterHook.activeTab === COUPON_TABS.DOWNLOADED} onClick={() => filterHook.setActiveTab(COUPON_TABS.DOWNLOADED)} />
                </Box>
                
                <Box testId={generateTestId(testID, 'search-filter-container')} sx={{ display: 'flex', alignItems: 'stretch', gap: `${spacing.sm}px`, width: { xs: '100%', sm: 'auto', md: 'auto' }, flexShrink: 0, minWidth: 0 }}>
                <Box testId={generateTestId(testID, 'search-container')} sx={{ display: 'flex', alignItems: 'center', flex: { xs: 1, sm: '0 1 auto', md: '0 1 auto' }, backgroundColor: colors.palette.white, gap: `${spacing.sm}px`, border: `1px solid ${colors.system.border.default}`, borderRadius: `${radius.md}px`, padding: `${spacing.sm}px ${spacing.md}px`, minWidth: { xs: 'auto', sm: 250, md: 350 }, maxWidth: { xs: '100%', sm: 400, md: 500 } }}>
                    <IconHot src={SearchIconSvg} size={spacing.xl} />
                    <InputBase 
                    testId={generateTestId(testID, 'search-input')}
                    placeholder={translate('my_coupons_personal_info_search_placeholder')}
                    value={filterHook.searchText} 
                    onChange={(e) => filterHook.setSearchText(e.target.value)} 
                    sx={{ flex: 1, fontSize: TypographyVariant.BodyMedium.fontSize }} 
                    />
                    {filterHook.searchText && (
                        <IconButton 
                            testId={generateTestId(testID, 'clear-search-btn')}
                            onClick={() => filterHook.setSearchText('')}
                            size="small"
                            sx={{ 
                                padding: `${spacing.xs}px`,
                                color: colors.system.text.secondary,
                                '&:hover': {
                                    color: colors.system.text.primary,
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                {filterHook.activeTab === COUPON_TABS.PAID && (
                    <Badge 
                        invisible={filterHook.appliedFilters?.selectedFilter === FILTER_BENEFITS_TYPES.ACTIVE}
                        badgeContent={'!'}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            '& .MuiBadge-badge': {
                                backgroundColor: colors.system.brand.primary,
                                minWidth: '20px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                padding: 0,
                                left: '-4px',
                                top: '10px',
                                color: colors.palette.white,
                                boxShadow: `0 0 0 1px ${colors.system.border.default}`,
                            }
                        }}
                    >
                        <IconButton 
                            testId={generateTestId(testID, 'filter-btn')} 
                            onClick={(e) => { filterHook.setFilterAnchorEl(e.currentTarget); filterHook.setFilterOpen(true); }}
                            sx={{
                                paddingTop: 0,
                                paddingBottom: 0,
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <IconHot src={FilterIconSvg} size={50} />
                        </IconButton>
                    </Badge>
                )}
                </Box>
            </Box>
        </Box>
    );
};